import { MIN_PASSWORD_LENGTH, passwordProblems } from '@wms/domain';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../api';
import { navigate } from '../router';
import { useSession } from '../session';
import { useToast } from '../ui';
import { Icon } from '../ui/icons';

/**
 * The user's own new password. Shown instead of the app while a temporary password (set by an
 * admin or the company's yönetici) is in use, and from the menu any time.
 */
export function ChangePassword({ forced = false }: { forced?: boolean }) {
  const session = useSession();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const problems = next ? passwordProblems(next, session.me.email) : [];
  const mismatch = again.length > 0 && again !== next;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (problems.length > 0 || mismatch || !current) return;
    setBusy(true);
    setError(null);
    try {
      // the account's other sessions end; this one goes on with the token it gets back
      const { token } = await api<{ token: string }>('POST', '/api/auth/password', { currentPassword: current, newPassword: next });
      session.renew(token);
      await session.reload();
      toast('Şifreniz değiştirildi', 'ok');
      if (!forced) navigate('/');
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  const form = (
    <form className="card-b stack" onSubmit={(e) => void submit(e)}>
      {forced && (
        <div className="banner warn">
          <Icon name="key" />
          <div>Hesabınız size verilen geçici şifreyle açıldı. Devam etmeden önce yalnız sizin bildiğiniz yeni bir şifre belirleyin.</div>
        </div>
      )}
      {error && <div className="banner bad">{error}</div>}
      <div className="field">
        <label htmlFor="pw-current">{forced ? 'Geçici şifre' : 'Mevcut şifre'}</label>
        <input id="pw-current" className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor="pw-next">Yeni şifre</label>
        <input id="pw-next" className="input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
        <span className={`hint${problems.length > 0 ? ' bad' : ''}`}>
          {problems.length > 0 ? problems.join(' · ') : `En az ${MIN_PASSWORD_LENGTH} karakter, harf ve rakam`}
        </span>
      </div>
      <div className="field">
        <label htmlFor="pw-again">Yeni şifre (tekrar)</label>
        <input id="pw-again" className="input" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} required />
        {mismatch && <span className="hint bad">Şifreler aynı değil</span>}
      </div>
      <div className="row">
        <button className="btn primary" disabled={busy || problems.length > 0 || mismatch || !current || !again}>
          Şifreyi değiştir
        </button>
        {forced ? (
          <button type="button" className="btn ghost" onClick={session.logout}>
            Çıkış yap
          </button>
        ) : (
          <a className="btn ghost" href="#/">
            Vazgeç
          </a>
        )}
      </div>
    </form>
  );

  if (!forced) return <section className="card narrow">{form}</section>;
  return (
    <div className="login">
      <div className="card">
        <div className="card-h">
          <h2>Yeni şifre belirleyin</h2>
          <span className="sub">{session.me.email}</span>
        </div>
        {form}
      </div>
    </div>
  );
}
